/**
 * Raw font-scale tokens. No MUI imports.
 * Referenced by createAppTheme.ts to build the MUI typography config.
 *
 * FIGMA reference:
 *  https://www.figma.com/design/CqO24eewdxuBg3MkLbj2QX/BHB-components?node-id=6112-51415&t=cqV7qec26xmWuSV7-0
 */
import { createTheme } from "@mui/material/styles";

import { colorTokens } from "./colors";

const baseTheme = createTheme();

export enum FontWeight {
  light = 300,
  regular = 400,
  medium = 500,
  semiBold = 600,
  bold = 700
}

export enum FontSize {
  xs = "11px",
  sm = "12px",
  md = "13px",
  base = "14px",
  lg = "16px",
  xl = "18px",
  xl2 = "20px",
  xl3 = "24px",
  xl4 = "28px",
  xl5 = "32px",
  xl6 = "34px"
}

/**
 * Variant definitions consumed directly by createAppTheme.ts.
 * Each key maps to a custom MUI variant declared in types.d.ts.
 */
export const typographyVariants = {
  h1: {
    fontSize: FontSize.xl3,
    fontWeight: FontWeight.light,
    color: colorTokens.primary,
    lineHeight: "1.17",
    letterSpacing: "0px"
  },
  h2: {
    fontSize: FontSize.xl2,
    fontWeight: FontWeight.medium,
    color: colorTokens.textPrimary,
    lineHeight: "1.6",
    letterSpacing: "0.15px"
  },
  h3: {
    fontSize: FontSize.xl2,
    fontWeight: FontWeight.light,
    color: colorTokens.textPrimary,
    lineHeight: "1.6",
    letterSpacing: "0.15px"
  },
  h4: {
    fontSize: FontSize.lg,
    fontWeight: FontWeight.medium,
    color: colorTokens.textSecondary,
    lineHeight: "1.57",
    letterSpacing: "0.1px"
  },
  body1: {
    fontSize: FontSize.base,
    fontWeight: FontWeight.regular,
    color: colorTokens.textPrimary,
    lineHeight: "1.5",
    letterSpacing: "0.15px",
    [baseTheme.breakpoints.down("md")]: {
      fontSize: FontSize.lg,
      letterSpacing: "0.4px"
    }
  },
  body2: {
    fontSize: FontSize.base,
    fontWeight: FontWeight.regular,
    color: colorTokens.textSecondary,
    lineHeight: "1.5",
    letterSpacing: "0.4px"
  },
  caption: {
    fontSize: FontSize.sm,
    fontWeight: FontWeight.regular,
    color: colorTokens.textSecondary
  },
  bodyHeader: {
    fontSize: FontSize.base,
    fontWeight: FontWeight.semiBold,
    color: colorTokens.textPrimary,
    lineHeight: "1.5",
    letterSpacing: "0.15px"
  },
  dialogHeader: {
    fontSize: FontSize.xl3,
    fontWeight: FontWeight.regular,
    color: colorTokens.primary,
    lineHeight: "1.33",
    letterSpacing: "0px"
  },
  dialogSubHeader: {
    fontSize: FontSize.lg,
    fontWeight: FontWeight.regular,
    color: colorTokens.textPrimary,
    lineHeight: "1.5",
    letterSpacing: "0.15px"
  },
  dialogBody: {
    fontWeight: FontWeight.regular,
    color: colorTokens.textPrimary,
    lineHeight: "1.5",
    letterSpacing: "0.15px",

    fontSize: FontSize.lg,
    [baseTheme.breakpoints.up("md")]: {
      fontSize: FontSize.base
    }
  },
  button: {
    fontSize: "14px",
    fontWeight: FontWeight.medium,
    textTransform: "none"
  },

  /** Small caps label — form field labels, table headers */
  label: {
    fontSize: FontSize.sm,
    fontWeight: FontWeight.semiBold,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const
  },
  /** Helper / caption — below inputs, in tooltips */
  helper: {
    fontSize: FontSize.xs,
    fontWeight: FontWeight.regular,
    lineHeight: 1.5
  }
} as const;
